//@ts-check
import Axios from "axios";
import Store from "../store";
import { isEqual } from "lodash-es";
import api from "./axios-interceptor";

class RequestHelperClass {
  constructor() {
    this.getAllPagesCache = [];
    this.enabled = false;
    this.baseUrl = process.env.VUE_APP_API_URL;
    this.baseUrlJava = process.env.VUE_APP_JAVA_API_URL;
  }

  getToken() {
    return Store.state.general.token;
  }

  getTokenKeycloak() {
    return Store.state.general.tokenKeycloak;
  }

  getRefreshTokenKeycloak() {
    return Store.state.general.refreshTokenKeycloak;
  }

  /**
   * @param {string} url
   * @param {object} [params]
   * @param {string|string[]|null} [siteTimezone = null]
   */
  async get(url, params, siteTimezone = null) {
    let cached = this.getCache(url, params);
    if (cached) {
      if (cached.data) {
        return cached.data;
      } else {
        let promise = new Promise(r => {
          cached.callbacks.push(r);
        });
        let data = await promise;
        return data;
      }
    }
    siteTimezone = this._getTimeZone(siteTimezone);
    const manyTimezones = Array.isArray(siteTimezone);
    let error = null;
    let request = await Axios.get(this.baseUrl + url, {
      params: {
        ...params,
        timezone: this._getTimeZone(),
        site_timezone: manyTimezones ? null : siteTimezone,
        site_timezones: manyTimezones ? siteTimezone.join(",") : null
      },
      headers: { Authorization: "Bearer " + this.getToken() }
    }).catch(e => (error = e.response));
    let cache = this.addCache(url, params);
    if (request == undefined) {
      return {
        status: 500
      };
    }

    if (cache && !error) {
      cache.data = request.data;
      for (let c of cache.callbacks) {
        c(request.data);
      }
    }
    return error || request.data || request;
  }

  /**
   * @param {string} url
   * @param {any} data
   * @param {object} [params]
   */
  async post(url, data, params) {
    return await Axios.post(this.baseUrl + url, data, {
      params,
      headers: { Authorization: "Bearer " + this.getToken() }
    });
  }

  /**
   * @param {string} url
   * @param {any} data
   * @param {object} [params]
   */
  async patch(url, data, params) {
    let error = null;
    let request = await Axios.patch(this.baseUrl + url, data, {
      params,
      headers: { Authorization: "Bearer " + this.getToken() }
    }).catch(e => (error = e.response));
    return error || request.data || request;
  }

  /**
   * @param {string} url
   * @param {any} data
   * @param {object} [params]
   */
  async patchJava(url, data, params) {
    let error = null;
    let request = await api
      .patch(this.baseUrlJava + url, data, {
        params,
        headers: {
          "Content-Type": "application/json-patch+json"
        }
      })
      .catch(e => (error = e.response));

    return error || request;
  }

  async patchJavaNoJsonPatch(url, params) {
    let error = null;
    let request = await api
      .patch(this.baseUrlJava + url, {
        params,
        headers: {
          "Content-Type": "application/json"
        }
      })
      .catch(e => (error = e.response));

    return error || request;
  }

  /**
   * @param {string} url
   */
  async delete(url) {
    return await Axios.delete(this.baseUrl + url, {
      headers: { Authorization: "Bearer " + this.getToken() }
    });
  }

  async deleteJava(url, data) {
    return await api.delete(this.baseUrlJava + url, {
      headers: {
        "Content-Type": "application/json"
      },
      data: data
    });
  }

  async postJava(url, data) {
    return await api.post(this.baseUrlJava + url, data, {
      headers: {
        "Content-Type": "application/json"
      }
    });
  }

  /**
   * @param {string} url
   * @param {object} [params]
   */
  async getJava(url, params) {
    let error = null;
    let request = await api
      .get(this.baseUrlJava + url, {
        params,
        headers: {
          "Content-Type": "application/json"
        }
      })
      .catch(e => (error = e.response));
    return error || request.data || request;
  }

  /**
   * @param {string} url
   * @param {string} [sort='name']
   * @param {string} [direction='asc']
   * @param {Function} [pageCallback]
   * @param {string} [dataProperty='content']
   */
  async getAllPagesJava(
    url,
    sort = "name",
    direction = "asc",
    pageCallback,
    dataProperty = "content"
  ) {
    let cached = this.getCache(url, { sort, direction });
    if (cached) {
      if (cached.data) {
        pageCallback?.(1, 1);
        return cached.data;
      } else {
        let data = await new Promise(r => cached.callbacks.push(r));
        pageCallback?.(1, 1);
        return data;
      }
    }

    let cache = this.addCache(url, { sort, direction });
    let allData = [];
    let totalPages = 1;

    // Get first page to determine total pages
    let response = await this.getJava(url, {
      page: 0,
      size: 1000,
      sort,
      direction
    });

    allData = [...response[dataProperty]];
    totalPages = response.totalPages;
    pageCallback?.(1, totalPages);

    if (totalPages > 1) {
      let pagesLoaded = 1;
      const requests = Array.from({ length: totalPages - 1 }, (_, i) =>
        Axios.get(this.baseUrlJava + url, {
          params: { page: i + 1, size: 1000, sort, direction },
          headers: {
            "Content-Type": "application/json"
          }
        }).then(res => (pageCallback?.(++pagesLoaded, totalPages), res))
      );

      const results = await Promise.all(requests);
      allData = [...allData, ...results.flatMap(r => r.data[dataProperty])];
    }

    if (cache) {
      cache.data = allData;
      cache.callbacks.forEach(c => c(allData));
    }
    return allData;
  }

  /**
   * @param {string} url
   * @param {object} [params]
   * @param {Function} [pageCallback]
   */
  async getAllPages(url, params, pageCallback) {
    let cached = this.getCache(url, params);
    if (cached) {
      if (cached.data) {
        if (pageCallback) {
          pageCallback(1, 1);
        }
        return cached.data;
      } else {
        let promise = new Promise(r => {
          cached.callbacks.push(r);
        });
        let data = await promise;
        if (pageCallback) {
          pageCallback(1, 1);
        }
        return data;
      }
    }
    let cache = this.addCache(url, params);
    let start = Date.now();
    let error = null;
    let request = await Axios.get(this.baseUrl + url, {
      params: {
        ...params,
        page: 1,
        per_page: 1000,
        timezone: this._getTimeZone()
      },
      headers: { Authorization: "Bearer " + this.getToken() }
    }).catch(e => (error = e.response));

    if (error) return error;

    let end = Date.now();
    let data = request.data.data;
    let pagesLoaded = 1;

    if (pageCallback) {
      pageCallback(pagesLoaded, request.data.meta.pages);
    }
    if (request.data.meta.pages > 1) {
      let requests = [];
      for (let i = 2; i <= request.data.meta.pages; i++) {
        requests.push(
          Axios.get(this.baseUrl + url, {
            params: {
              ...params,
              page: i,
              per_page: 1000,
              timezone: this._getTimeZone()
            },
            headers: { Authorization: "Bearer " + this.getToken() }
          }).then(response => {
            if (pageCallback) {
              pageCallback(++pagesLoaded, request.data.meta.pages);
            }
            return response;
          })
        );
      }
      let results = await Promise.all(requests);
      end = Date.now();
      for (let r of results) {
        data = [...data, ...r.data.data];
      }
    }
    data.meta = request.data.meta;
    data.config = request.config;
    data.duration = end - start;

    if (cache) {
      cache.data = data;
      for (let c of cache.callbacks) {
        c(data);
      }
    }
    return data;
  }

  /**
   * @param {string} url
   * @param {object} [params]
   * @param {Function} [pageCallback]
   */
  async getAllLicensesFromJavaApi(url, params, pageCallback) {
    let cached = this.getCache(url, params);
    if (cached) {
      if (cached.data) {
        if (pageCallback) {
          pageCallback(1, 1);
        }
        return cached.data;
      } else {
        let promise = new Promise(r => {
          cached.callbacks.push(r);
        });
        let data = await promise;
        if (pageCallback) {
          pageCallback(1, 1);
        }
        return data;
      }
    }
    let cache = this.addCache(url, params);
    let start = Date.now();
    let error = null;

    const newParams = { view: "tenant", page: 0, size: 1000 };

    let request = await api
      .get(this.baseUrlJava + url, {
        params: {
          ...newParams
        },
        headers: { Accept: "application/json" }
      })
      .catch(e => (error = e.response));

    if (error) return error;

    let end = Date.now();
    let data = request.data.content;
    let pagesLoaded = 1;

    if (pageCallback) {
      pageCallback(pagesLoaded, request.data.totalPages);
    }
    if (request.data.totalPages > 1) {
      let requests = [];
      for (let i = 2; i <= request.data.totalPages; i++) {
        requests.push(
          api
            .get(this.baseUrlJava + url, {
              params: {
                ...newParams,
                page: i
              },
              headers: { Accept: "application/json" }
            })
            .then(response => {
              if (pageCallback) {
                pageCallback(++pagesLoaded, request.data.meta.pages);
              }
              return response;
            })
        );
      }
      let results = await Promise.all(requests);
      end = Date.now();
      for (let r of results) {
        data = [...data, ...r.data.content];
      }
    }

    const tenantCounts = data.reduce((acc, item) => {
      const tenant = item.tenant;
      if (!tenant) return acc;

      if (!acc[tenant.id]) {
        const availableLicences = item.silo == null ? 1 : 0;
        acc[tenant.id] = {
          ...tenant,
          total_licences: 1,
          available_licences: availableLicences,
          items: [item.id]
        };
      } else {
        acc[tenant.id].total_licences++;
        acc[tenant.id].items.push(item.id);
        if (item.silo == null) {
          acc[tenant.id].available_licences++;
        }
      }
      return acc;
    }, {});

    const result = Object.values(tenantCounts);

    result.meta = {
      total: request.data.totalElements,
      page_total: request.data.totalElements,
      pages: request.data.totalPages,
      per_page: request.data.size,
      current_page: request.data.number + 1,
      sort: request.data.sort,
      empty: request.data.empty,
      first: request.data.first,
      last_page: request.data.last
    };
    result.meta.pageable = request.data.pageable;
    result.config = request.config;
    result.duration = end - start;

    if (cache) {
      cache.data = result;
      for (let c of cache.callbacks) {
        c(result);
      }
    }
    return result;
  }

  /**
   * @param {string} url
   * @param {object} [params]
   * @param {Function} [pageCallback]
   */
  async getSilosByPage(url, params, pageCallback) {
    let cached = this.getCache(url, params);
    if (cached) {
      if (cached.data) {
        if (pageCallback) {
          pageCallback(1, 1);
        }
        return cached.data;
      } else {
        let promise = new Promise(r => {
          cached.callbacks.push(r);
        });
        let data = await promise;
        if (pageCallback) {
          pageCallback(1, 1);
        }
        return data;
      }
    }
    let cache = this.addCache(url, params);
    let start = Date.now();
    let error = null;

    let request;
    try {
      if (params.page) {
        params.page -= 1;
      }
      request = await api.post(
        this.baseUrlJava + url,
        { view: "RESUME", ...params },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );
    } catch (e) {
      error = e.response;
    }

    if (error) return error;

    let end = Date.now();
    let result = request.data.content;

    result.forEach((data, index) => {
      if (data.silo_levels && data.silo_levels.length > 0) {
        data.last_level = data.silo_levels[0];
      } else {
        data.last_level = {
          battery: null,
          level: 0.0,
          temperature: null,
          rssi: null,
          snr: 0,
          level_at: null,
          v_silo: 0
        };
      }
      data.battery = data.last_level.battery;
      data.quantity = data.last_level.level;
      data.temperature = data.last_level.temperature;
      data.rssi = data.last_level.rssi;
      data.snr = data.last_level.snr;
      data.last_sync = data.last_level.level_at;
      data.module_version = data.last_level.v_silo;

      data.chart_silo = Array.isArray(data.chart_silo)
        ? data.chart_silo.map(item => [
            new Date(item.levelDateUtc).toISOString(),
            item.quantity
          ])
        : [];

      data.chart_battery = Array.isArray(data.chart_battery)
        ? data.chart_battery.map(item => [
            new Date(item.levelDateUtc).toISOString(),
            item.battery
          ])
        : [];

      if (data.computed_data && typeof data.computed_data === "object") {
        Object.entries(data.computed_data).forEach(([key, value]) => {
          data[key] = value;
        });
      }
    });
    result.config = request.config;
    result.duration = end - start;

    result.meta = {
      total: request.data.totalElements,
      page_total: request.data.totalElements,
      pages: request.data.totalPages,
      per_page: request.data.size,
      current_page: request.data.number + 1,
      sort: request.data.sort,
      empty: request.data.empty,
      first: request.data.first,
      last_page: request.data.last
    };
    result.meta.pageable = request.data.pageable;

    let pagesLoaded = 1;
    if (pageCallback) {
      pageCallback(pagesLoaded, request.data.totalPages);
    }
    if (cache) {
      cache.data = data;
      for (let c of cache.callbacks) {
        c(data);
      }
    }

    return result;
  }

  /**
   * @param {string} url
   * @param {object} params
   */
  getCache(url, params) {
    if (!this.enabled) {
      return null;
    }
    // Clear old locks
    this.getAllPagesCache = this.getAllPagesCache.filter(l => {
      return l.date >= Date.now() - 1000 * 10;
    });
    for (let cache of this.getAllPagesCache) {
      if (cache.url == url && isEqual(params, cache.params)) {
        return cache;
      }
    }
    return null;
  }

  /**
   * @param {string} url
   * @param {object} params
   * @param {any} [data]
   */
  addCache(url, params, data) {
    if (!this.enabled) {
      return null;
    }
    let cache = new RequestHelperCache(url, params, data);
    this.getAllPagesCache.push(cache);
    return cache;
  }

  /**
   * @param {string|string[]|null} [timezone = null]
   * @return {string|string[]} Timezone as Standard Time
   * @private
   */
  _getTimeZone(timezone = null) {
    if (
      timezone !== null &&
      timezone.trim() !== "" &&
      Array.isArray(timezone)
    ) {
      let timezones = [];
      for (const current of timezone) {
        timezones.push(this._getTimeZone(current));
      }
      return timezones;
    }
    if (timezone === null || timezone.trim() === "") {
      timezone = new Date()
        .toLocaleDateString("en-US", {
          day: "2-digit",
          timeZoneName: "long"
        })
        .slice(4);
    }
    return timezone.replaceAll("Daylight", "Standard");
  }
}

class RequestHelperCache {
  /**
   * @param {string} url
   * @param {object} params
   * @param {any} [data]
   */
  constructor(url, params, data) {
    this.url = url;
    this.params = params;
    this.data = data || null;
    this.callbacks = [];
    this.date = Date.now();
  }
}

let RequestHelper = new RequestHelperClass();
export default RequestHelper;
